/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

export const CARD_TYPE_STANDARD = "standard";
export const CARD_TYPE_INDUSTRY = "industry";

/**
 * @description Reads the Issue-to radio state from a loaded card. Any recipient group means an
 * industry card, matching how the backend derives card_type.
 * @param card The discount card returned by the show endpoint.
 * @returns The card type the form should show.
 */
export const resolveCardType = (card) => {
  return normalizeAudienceGroupOptions(card).length > 0
    ? CARD_TYPE_INDUSTRY
    : CARD_TYPE_STANDARD;
};

/**
 * @description Maps a loaded card's recipient groups onto the dropdown option shape so their
 * names render before any dropdown page is fetched.
 * @param card The discount card returned by the show endpoint.
 * @returns Dropdown options for the recipient groups.
 */
export const normalizeAudienceGroupOptions = (card) => {
  const audienceGroups = Array.isArray(card?.audience_restaurant_groups)
    ? card.audience_restaurant_groups
    : [];

  return audienceGroups
    .filter((group) => group?.id !== undefined && group?.id !== null)
    .map((group) => ({ value: group.id, label: group?.name || "" }));
};

/**
 * @description Builds the Issue-to payload list. A standard card always clears its recipients,
 * which is what returns an industry card to standard.
 * @param cardType The Issue-to radio value.
 * @param audienceRestaurantGroupIds The currently selected recipient group ids.
 * @returns The list to send as audience_restaurant_group_ids.
 */
export const buildAudienceRestaurantGroupIds = (cardType, audienceRestaurantGroupIds = []) => {
  return cardType === CARD_TYPE_INDUSTRY ? audienceRestaurantGroupIds : [];
};

/**
 * @description Tells whether recipient groups were added or removed since the card loaded.
 * Order does not matter.
 * @param initialGroups The recipient group options the card loaded with.
 * @param nextGroupIds The recipient group ids about to be submitted.
 * @returns True when a group was added or removed.
 */
export const hasAudienceGroupChanges = (initialGroups = [], nextGroupIds = []) => {
  const initialIds = new Set(initialGroups.map((group) => String(group?.value)));
  const nextIds = new Set(nextGroupIds.map((groupId) => String(groupId)));

  if (initialIds.size !== nextIds.size) {
    return true;
  }

  return [...nextIds].some((groupId) => !initialIds.has(groupId));
};

/**
 * @description Applies the user's recipient changes onto the card's current list, so groups
 * another admin added or removed meanwhile are kept as they are.
 * @param initialGroups The recipient group options the card loaded with.
 * @param nextGroupIds The recipient group ids the user selected.
 * @param currentGroups The recipient group options the card has now.
 * @returns The recipient group ids to send.
 */
export const mergeAudienceGroupIds = (initialGroups = [], nextGroupIds = [], currentGroups = []) => {
  const initialIds = new Set(initialGroups.map((group) => String(group?.value)));
  const nextIds = new Set(nextGroupIds.map((groupId) => String(groupId)));
  const keptIds = currentGroups
    .map((group) => group?.value)
    .filter((groupId) => !initialIds.has(String(groupId)) || nextIds.has(String(groupId)));
  const keptIdSet = new Set(keptIds.map((groupId) => String(groupId)));
  const addedIds = nextGroupIds.filter(
    (groupId) => !initialIds.has(String(groupId)) && !keptIdSet.has(String(groupId)),
  );

  return [...keptIds, ...addedIds];
};

/**
 * @description Finds the recipient groups that the ids about to be submitted leave out.
 * @param groups The recipient group options to compare against.
 * @param nextGroupIds The recipient group ids about to be submitted.
 * @returns The group options that would be removed.
 */
export const findRemovedAudienceGroups = (groups = [], nextGroupIds = []) => {
  const keptIds = new Set(nextGroupIds.map((groupId) => String(groupId)));

  return groups.filter((group) => !keptIds.has(String(group?.value)));
};

/**
 * @description Names the recipient groups that the ids about to be submitted leave out, for the
 * confirm dialog.
 * @param groups The recipient group options to compare against.
 * @param nextGroupIds The recipient group ids about to be submitted.
 * @returns Names of the removed groups, falling back to the id when no name is known.
 */
export const findRemovedAudienceGroupNames = (groups = [], nextGroupIds = []) => {
  return findRemovedAudienceGroups(groups, nextGroupIds)
    .map((group) => group?.label || String(group?.value));
};

/**
 * @description Tells whether auto-assign can be on. It needs at least one recipient group,
 * matching the backend rule.
 * @param cardType The Issue-to radio value.
 * @param audienceRestaurantGroupIds The currently selected recipient group ids.
 * @returns True when the auto-assign switch may be on.
 */
export const canAutoAssignToAudience = (cardType, audienceRestaurantGroupIds = []) => {
  return cardType === CARD_TYPE_INDUSTRY && audienceRestaurantGroupIds.length > 0;
};

/**
 * @description Works out the auto-assign switch after an Issue-to change: off with no group,
 * on when the first group is picked unless the admin turned it off, otherwise unchanged.
 * @param current The switch value before the change.
 * @param previousGroupIds The recipient group ids before the change.
 * @param nextGroupIds The recipient group ids after the change.
 * @param manuallyOff Whether the admin turned the switch off.
 * @returns The switch value to apply.
 */
export const resolveAutoAssignOnGroupChange = ({ current, previousGroupIds = [], nextGroupIds = [], manuallyOff }) => {
  if (nextGroupIds.length === 0) return false;
  if (previousGroupIds.length === 0 && !manuallyOff) return true;
  return current;
};
